import EnteteBonEtFacture from '@/components/en-tete-bon-facture'
import FacturePatientBloc from '@/components/comptable/creance/facture-patient-bloc'
import { montantSelonMode, formaterMontant, montantEnLettres, mentionTaux } from '@/utils/facture-assurance'

export default {
    name: 'print-facture-assurance',
    // L'en-tête de l'établissement n'était pas déclaré : il n'apparaissait pas sur ces factures (05/10/2026).
    components: { EnteteBonEtFacture, FacturePatientBloc },
    props: {
        is_assurance_bill: {
            type: Boolean,
            default: true
        },
        selected_facture: {
            type: Object,
            default: () => {}
        },
        view_by_societe: {
            type: Array,
            default: []
        },
        view_patients: {
            type: Array,
            default: []
        },
        total_facture: {
            type: Number,
            default: 0
        },
        total_en_lettre: {
            type: String,
            default: ''
        },
        // by_societe, by_patient, ou individuel : une facture par patient coché, chacune sur sa page (05/10/2026)
        selected_view: {
            type: String,
            default: ''
        },
        // principale, secondaire, principale_conversion, secondaire_conversion (05/10/2026)
        devise_mode: {
            type: String,
            default: 'principale'
        },
        type_facture: {
            type: String,
            default: ''
        },
        print_toggler: Number
    },
    data() {
        return {
            can_print: false,
            can_print_soc: false,
            footer_image_failed: false,
            perPage: -1,
            print_per_page: -1,
            currentPage: 1
        }
    },

    watch: {
        print_toggler() {
            if ( this.type_facture == 'facture_assurance') {
                this.printCreanceFacture()
            } else {
                this.printFactureSociete()
            }
        }
    },

    computed: {
        conversion() {
            return montantSelonMode(0, this.devise_mode, this.currency_info).conversion
        },

        mention_taux() {
            return mentionTaux(this.devise_mode, this.currency_info)
        }
    },

    methods: {
        waitForInvoiceImages(root) {
            if (!root) return Promise.resolve()
            const images = Array.from(root.querySelectorAll('img'))
            return Promise.all(images.map(image => {
                const isFooter = !!image.closest('.assurance-invoice-footer')
                if (image.complete) {
                    if (isFooter && !image.naturalWidth) this.footer_image_failed = true
                    return Promise.resolve()
                }
                return new Promise(resolve => {
                    let timeoutId
                    const done = () => {
                        if (isFooter && image.complete && !image.naturalWidth) this.footer_image_failed = true
                        image.removeEventListener('load', done)
                        image.removeEventListener('error', done)
                        clearTimeout(timeoutId)
                        resolve()
                    }
                    image.addEventListener('load', done)
                    image.addEventListener('error', done)
                    timeoutId = setTimeout(() => {
                        if (isFooter) this.footer_image_failed = true
                        done()
                    }, 3500)
                    if (image.complete) done()
                })
            }))
        },

        // Le pied est dans le flux. Une facture courte complète sa première
        // feuille ; près d'une coupure, les dernières lignes, le total et le
        // pied sont placés ensemble sur la feuille suivante.
        preparerPagesFacture(root) {
            const mm = 96 / 25.4
            const hauteurFeuille = 287 * mm // @page réserve 10 mm en haut.
            const garde = 3 * mm
            const pages = Array.from(root.querySelectorAll('.assurance-invoice-page'))
            const restaurations = []
            pages.forEach(page => {
                page.style.removeProperty('min-height')
                page.style.paddingBottom = '0'
                const pied = Array.from(page.children).find(child => child.classList.contains('assurance-invoice-footer'))
                if (!pied) return
                pied.style.marginTop = '0px'
                const hautPage = page.getBoundingClientRect().top
                const basPied = pied.getBoundingClientRect().bottom - hautPage
                const nombre = Math.max(1, Math.ceil((basPied + garde) / hauteurFeuille))
                const resume = page.querySelector('.assurance-invoice-final-summary')
                const hautResume = resume && resume.getBoundingClientRect().top - hautPage
                const procheCoupure = nombre * hauteurFeuille - basPied < 12 * mm
                const aReporter = nombre === 1 && resume && hautResume < hauteurFeuille && procheCoupure
                if (aReporter) {
                    const lignes = Array.from(page.querySelectorAll('.assurance-invoice-table tbody tr')).filter(row =>
                        row.querySelector('td') && !row.classList.contains('assurance-invoice-subtotal-row') &&
                        !row.classList.contains('assurance-invoice-patient-total-row') &&
                        !row.classList.contains('assurance-invoice-company-total-row'))
                    const derniereTable = lignes.length && lignes[lignes.length - 1].closest('table')
                    const lignesTable = derniereTable && lignes.filter(row => row.closest('table') === derniereTable)
                    if (lignesTable && lignesTable.length >= 4) {
                        const debut = lignesTable[Math.max(0, lignesTable.length - Math.min(20, Math.max(4, Math.ceil(lignes.length / 3))))]
                        const suite = document.createElement('div')
                        suite.className = 'assurance-invoice-continuation'
                        const tableSuite = derniereTable.cloneNode(false)
                        const entete = derniereTable.querySelector('thead')
                        if (entete) tableSuite.appendChild(entete.cloneNode(true))
                        const deplacements = []
                        const corps = Array.from(derniereTable.querySelectorAll('tbody'))
                        let copier = false
                        corps.forEach(tbody => {
                            const nouveauCorps = tbody.cloneNode(false)
                            const lignesCorps = Array.from(tbody.children)
                            const indexDebut = lignesCorps.indexOf(debut)
                            if (indexDebut > 0) {
                                const titreRubrique = lignesCorps.slice(0, indexDebut).reverse().find(ligne =>
                                    ligne.classList.contains('assurance-invoice-section-row'))
                                if (titreRubrique) nouveauCorps.appendChild(titreRubrique.cloneNode(true))
                            }
                            lignesCorps.forEach(ligne => {
                                if (ligne === debut) copier = true
                                if (copier) {
                                    deplacements.push({ ligne, tbody })
                                    nouveauCorps.appendChild(ligne)
                                }
                            })
                            if (nouveauCorps.children.length) tableSuite.appendChild(nouveauCorps)
                        })
                        suite.appendChild(tableSuite)
                        const pointInsertion = derniereTable.closest('.mb-5') || derniereTable.parentNode
                        pointInsertion.parentNode.insertBefore(suite, pointInsertion.nextSibling)
                        const resumeParent = resume.parentNode
                        const resumeSuivant = resume.nextSibling
                        const piedParent = pied.parentNode
                        const piedSuivant = pied.nextSibling
                        suite.appendChild(resume)
                        suite.appendChild(pied)
                        restaurations.push(() => {
                            deplacements.forEach(({ ligne, tbody }) => tbody.appendChild(ligne))
                            resumeParent.insertBefore(resume, resumeSuivant && resumeSuivant.parentNode === resumeParent ? resumeSuivant : null)
                            piedParent.insertBefore(pied, piedSuivant && piedSuivant.parentNode === piedParent ? piedSuivant : null)
                            suite.remove()
                        })
                        return
                    }
                }
                // Chaque feuille suivante répète la marge haute et l'en-tête
                // du tableau : la mesure écran doit en tenir compte.
                const gardeFinale = (3 + 15 * (nombre - 1)) * mm
                pied.style.marginTop = `${Math.max(0, nombre * hauteurFeuille - basPied - gardeFinale)}px`
            })
            return () => {
                restaurations.reverse().forEach(restaurer => restaurer())
                pages.forEach(page => {
                    page.style.removeProperty('min-height')
                    page.style.removeProperty('padding-bottom')
                    const pied = Array.from(page.children).find(child => child.classList.contains('assurance-invoice-footer'))
                    if (pied) pied.style.removeProperty('margin-top')
                })
            }
        },

        async attendreMiseEnPageFacture() {
            if (document.fonts && document.fonts.ready) await document.fonts.ready
            await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
        },

        styleFeuilleFacture() {
            const style = document.createElement('style')
            style.textContent = '@page { size: A4; margin: 10mm 0 0; }'
            document.head.appendChild(style)
            return style
        },

        montant(valeur) {
            return formaterMontant(montantSelonMode(valeur, this.devise_mode, this.currency_info))
        },

        montantConverti(valeur) {
            const resultat = montantSelonMode(valeur, this.devise_mode, this.currency_info)
            return resultat.conversion ? formaterMontant(resultat.conversion) : ''
        },

        enLettres(valeur) {
            return montantEnLettres(montantSelonMode(valeur, this.devise_mode, this.currency_info))
        },

        printCreanceFacture() {
            this.footer_image_failed = false
            this.can_print = true

            if (this.selected_view === 'individuel') {
                this.$nextTick(() => this.imprimerFacturesIndividuelles())
                return
            }

            this.$nextTick(() => this.imprimerFactureGroupee('.print-fassur', 'can_print'))
        },

        // Les éléments masqués par visibility:hidden conservent leur hauteur dans
        // Chromium et pouvaient générer une deuxième page vierge. Pendant
        // l'impression, seule la facture est dans le flux de body.
        imprimerFactureGroupee(selecteur, indicateur) {
            const bloc = this.$el.querySelector(selecteur)
            if (!bloc) { this[indicateur] = false; return }
            const parent = bloc.parentNode
            const suivant = bloc.nextSibling
            const portail = document.createElement('div')
            portail.className = 'print-facture-assurance impression-portail'
            portail.appendChild(bloc)
            document.body.appendChild(portail)
            document.documentElement.classList.add('impression-facture-assurance')
            setTimeout(async () => {
                let nettoyerPages = () => {}
                let papier
                try {
                    await this.waitForInvoiceImages(bloc)
                    await this.$nextTick()
                    await this.attendreMiseEnPageFacture()
                    nettoyerPages = this.preparerPagesFacture(portail)
                    papier = this.styleFeuilleFacture()
                    window.print()
                } finally {
                    nettoyerPages()
                    if (papier) papier.remove()
                    document.documentElement.classList.remove('impression-facture-assurance')
                    if (parent) parent.insertBefore(bloc, suivant && suivant.parentNode === parent ? suivant : null)
                    portail.remove()
                    this[indicateur] = false
                }
            }, 500)
        },

        // Une facture par patient, avec saut avant la suivante ; une facture longue
        // peut occuper plusieurs feuilles. Electron ignore les sauts dans un bloc
        // absolu : le portail est placé sous <body> le temps de l'impression.
        imprimerFacturesIndividuelles() {
            const bloc = this.$refs.factures_individuelles
            if (!bloc) { this.can_print = false; return }
            const parent = bloc.parentNode
            const suivant = bloc.nextSibling
            bloc.classList.add('impression-portail')
            document.body.appendChild(bloc)
            document.documentElement.classList.add('impression-individuelle')
            setTimeout(async () => {
                let nettoyerPages = () => {}
                let papier
                try {
                    await this.waitForInvoiceImages(bloc)
                    await this.$nextTick()
                    await this.attendreMiseEnPageFacture()
                    nettoyerPages = this.preparerPagesFacture(bloc)
                    papier = this.styleFeuilleFacture()
                    window.print()
                } finally {
                    nettoyerPages()
                    if (papier) papier.remove()
                    document.documentElement.classList.remove('impression-individuelle')
                    bloc.classList.remove('impression-portail')
                    if (parent) parent.insertBefore(bloc, suivant && suivant.parentNode === parent ? suivant : null)
                    this.can_print = false
                }
            }, 500);
        },

        printFactureSociete() {
            this.footer_image_failed = false
            this.can_print_soc = true
            this.$nextTick(() => this.imprimerFactureGroupee('.print-fsociete', 'can_print_soc'))
        }
    }
}
